import { createSelector } from '@reduxjs/toolkit'
import { USER_TYPES } from 'constants/users'
import {
  MARKET_ROUND_TEAM_PAIRS,
  MARKET_ROUND_TEAM_COLOR_FALLBACK
} from 'constants/marketRoundTeamColors'
import { TEAM_VISUAL_MAX_SLOTS, buildTeamSlotMap } from 'utils/teamVisualIdentity'

export const facilitatorEmailsForGame = (game) =>
  new Set(
    (game?.facilitators || [])
      .map((facilitator) => facilitator?.email?.toLowerCase?.())
      .filter(Boolean)
  )

/** Games created from playlearning-admin UI (non-external) — see API `Game::GAME_ORIGINS`. */
export const isAdminCreatedPlaylearningGame = (game) =>
  game != null && game.admin_game_id != null && game.origin === 'admin'

export const isFacilitatorVsPlayersGame = (game) => {
  const facilitatorEmails = facilitatorEmailsForGame(game)
  if (facilitatorEmails.size === 0) return false

  return (game?.teams || []).some((team) =>
    (team?.players || []).some((player) => {
      const email = player?.email?.toLowerCase?.()
      return email && !facilitatorEmails.has(email)
    })
  )
}

// Game selectors
export const gameIdSelector = createSelector(
  (state) => state.user.game.data,
  (game) => game?.id
)

export const gameAiSelector = createSelector(
  (state) => state.user.game.data,
  (game) => game?.ai
)

export const gameSettingsSelector = createSelector(
  (state) => state.user.game.data?.game_setting,
  (settings) => settings?.setup
)

export const gameTeamsSelector = createSelector(
  (state) => state.user.game.data,
  (game) => {
    if (!game?.teams) return []
    const teams = [...game.teams]
    return teams.sort((a, b) => (a.position ?? a.id) - (b.position ?? b.id))
  }
)

/**
 * Slot + primary/secondary per team id (lobby slot / backend position / color / name).
 * Pure resolver — no Lobby ref; renamed teams without position rely on `team.color` or name pattern.
 */
export const teamVisualsByIdSelector = createSelector(gameTeamsSelector, (teams) => {
  const slotMap = buildTeamSlotMap(teams, null)
  const byId = {}
  teams.forEach((team) => {
    const rawSlot = slotMap.get(String(team.id)) ?? 1
    const slot = Math.max(1, Math.min(TEAM_VISUAL_MAX_SLOTS, rawSlot))
    const pair = MARKET_ROUND_TEAM_PAIRS[(slot - 1) % MARKET_ROUND_TEAM_PAIRS.length]
    byId[team.id] = { slot, primary: pair.primary, secondary: pair.secondary }
  })
  return byId
})

export const gameTeamColorsSelector = createSelector(
  gameTeamsSelector,
  teamVisualsByIdSelector,
  (teams, visuals) =>
    teams.map((team) => visuals[team.id]?.primary ?? MARKET_ROUND_TEAM_COLOR_FALLBACK)
)

/** Primary color only (lobby-aligned). */
export const marketRoundTeamColorByIdSelector = createSelector(teamVisualsByIdSelector, (visuals) =>
  Object.fromEntries(Object.entries(visuals).map(([id, { primary }]) => [Number(id), primary]))
)

/** Primary + secondary per team id for Market Round TeamBox. */
export const marketRoundTeamColorPairsByIdSelector = createSelector(
  teamVisualsByIdSelector,
  (visuals) =>
    Object.fromEntries(
      Object.entries(visuals).map(([id, pair]) => [
        Number(id),
        { primary: pair.primary, secondary: pair.secondary }
      ])
    )
)

export const gameCurrentGraphic = createSelector(
  (state) => state.user.game.data,
  (game) => game.current_graphic
)

export const sharedGraphicsModeSelector = createSelector(
  (state) => state.user.game.data,
  (game) => !game?.ai && isFacilitatorVsPlayersGame(game)
)

export const currentUserIsGameFacilitatorSelector = createSelector(
  (state) => state.user.info.data,
  (state) => state.user.game.data,
  (user, game) => {
    const email = user?.email?.toLowerCase?.()
    return email ? facilitatorEmailsForGame(game).has(email) : false
  }
)

export const canCurrentUserControlJoinedGraphicsSelector = createSelector(
  gameAiSelector,
  sharedGraphicsModeSelector,
  (state) => state.user.info.data,
  currentUserIsGameFacilitatorSelector,
  (isAiGame, useSharedGraphics, user, isGameFacilitator) =>
    isAiGame ||
    !useSharedGraphics ||
    user?.user_type === USER_TYPES.facilitator ||
    isGameFacilitator
)

// Team selectors
export const currentActionSelector = createSelector(
  (state) => state.user.team.data,
  (team) => team?.action_state
)

export const currentGeneralStateSelector = createSelector(
  (state) => state.user.team.data,
  (team) => team?.general_state
)

export const currentGameStateSelector = createSelector(
  (state) => state.user.game.data,
  (game) => game.aasm_state
)

export const currentYearTeamSelector = createSelector(
  (state) => state.user.team.data,
  (team) => team?.current_year
)

export const tutorialYearTeamSelector = createSelector(
  (state) => state.user.team.data,
  (state) => state.user.game.data?.starting_year,
  (team, startingYear) => team?.current_year === (startingYear ?? 1)
)

// Bank selectors
export const cashSelector = createSelector(
  (state) => state.user.bank.data,
  (bank) => bank?.cash
)

export const accountReceivablesSelector = createSelector(
  (state) => state.user.bank.data,
  (bank) => bank?.account_receivables
)

// User selectors
export const userTypeSelector = createSelector(
  (state) => state.user.info.data,
  (user) => user.user_type
)

// Onboarding + tutorial progress selectors
export const GAMES_ENTERED_FOR_ONBOARDING = 2
export const YEAR_ONE_COMPLETIONS_FOR_SKIP_TUTORIAL = 2

export const gamesEnteredCountSelector = createSelector(
  (state) => state.user.info.data,
  (user) => user?.games_entered_count ?? 0
)

export const yearOneCompletionsCountSelector = createSelector(
  (state) => state.user.info.data,
  (user) => user?.year_one_completions_count ?? 0
)

export const canSkipTutorialSelector = createSelector(
  (state) => state.user.info.data,
  (user) => {
    if (user?.can_skip_tutorial !== undefined) return Boolean(user.can_skip_tutorial)
    const count = user?.year_one_completions_count ?? 0
    return count >= YEAR_ONE_COMPLETIONS_FOR_SKIP_TUTORIAL
  }
)

// NOTE(GAM-268): When an Auth0 User creates/joins a game, the API swaps the
// User token for a Player token (see Users::SessionsController#sign_in_player_or_facilitator),
// so `/user` comes back as user_type = Player. The Player is linked back to
// the underlying User via `players.user_id` (lazy-backfilled on sign-in), and
// PlayerSerializer exposes games_entered_count / year_one_completions_count /
// can_skip_tutorial from
// Player#linked_user.
// Post-login tour runs until the user has entered GAMES_ENTERED_FOR_ONBOARDING
// started games.
// We do NOT show the tour for pure access-code Players (user_type = Player and
// user_id is null): they log in via access code, bypass /game-selection, and
// shouldn't see the onboarding flow.
export const shouldShowPostLoginOnboardingSelector = createSelector(
  (state) => state.auth.loggedIn,
  (state) => state.user.info.data,
  (loggedIn, user) => {
    if (!loggedIn || !user) return false
    const count = user.games_entered_count ?? 0
    if (count >= GAMES_ENTERED_FOR_ONBOARDING) return false
    if (user.user_type === USER_TYPES.player && !user.user_id) return false
    return true
  }
)

export const isFacilitatorSelector = createSelector(
  (state) => state.user.info.data,
  (user) => user?.user_type === USER_TYPES.facilitator
)

export const isPlayerSelector = createSelector(
  (state) => state.user.info.data,
  (user) => user?.user_type === USER_TYPES.player
)

export const isCurrentPlayerSelector = createSelector(
  (state) => state.user.info.data,
  (state) => state.user.team.data?.current_player,
  (user, currentPlayer) => {
    if (!user || !currentPlayer) return false
    // Seeds/legacy: player has no user_id; when logged in as a Player, compare player ids only then
    const isSeededOrLegacy = currentPlayer.user_id == null
    if (
      isSeededOrLegacy &&
      user.user_type === USER_TYPES.player &&
      user.id != null &&
      currentPlayer.id != null
    ) {
      return user.id === currentPlayer.id
    }
    // Authenticated User flow: compare user id to current player's user_id
    if (user.id != null && currentPlayer.user_id != null && user.id === currentPlayer.user_id) {
      return true
    }
    // Fallback: match by email when user_id is null (legacy players)
    return (
      user.email != null &&
      currentPlayer.email != null &&
      user.email.toLowerCase() === currentPlayer.email.toLowerCase()
    )
  }
)
